import { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { Helmet } from "react-helmet-async";
import {
    FaArrowLeft,
    FaArrowRight,
    FaCalendarAlt,
    FaCheck,
    FaChevronRight,
    FaClock,
    FaFire,
    FaHome,
    FaInfoCircle,
    FaMapMarkerAlt,
    FaShieldAlt,
    FaTag,
    FaUsers,
    FaBed,
    FaExpand,
    FaTimes,
} from "react-icons/fa";

const ROOM_IMG = "https://uaediscountedhotels.com/uploads/rooms";

export default function RoomDetail() {
    const { roomId } = useParams();
    const navigate = useNavigate();
    const { executeRecaptcha } = useGoogleReCaptcha();

    const [room, setRoom] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const [activeImage, setActiveImage] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [modalIndex, setModalIndex] = useState(0);
    const [showBookingModal, setShowBookingModal] = useState(false);

    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [checkIn, setCheckIn] = useState("");
    const [checkOut, setCheckOut] = useState("");
    const [guests, setGuests] = useState(1);
    const [bookingLoading, setBookingLoading] = useState(false);

    const today = new Date().toISOString().split("T")[0];
    const selectedDate = checkIn || today;

    useEffect(() => {
        document.body.style.overflow =
            showBookingModal || showModal ? "hidden" : "auto";

        return () => {
            document.body.style.overflow = "auto";
        };
    }, [showBookingModal, showModal]);

    useEffect(() => {
        const fetchRoom = async () => {
            try {
                setLoading(true);
                setError(false);

                const res = await axios.get(
                    `https://dubaihotelrates.com/api/rooms/${roomId}`
                );

                setRoom(res.data);
                setActiveImage(res.data?.image || null);
            } catch (err) {
                console.error(err);
                setError(true);
            } finally {
                setLoading(false);
            }
        };

        if (roomId) fetchRoom();
    }, [roomId]);

    if (loading) {
        return (
            <div className="min-h-screen bg-[#F4F8FC] flex items-center justify-center">
                <div className="text-center">
                    <div className="mx-auto h-12 w-12 rounded-full border-4 border-[#174EA6]/20 border-t-[#174EA6] animate-spin" />
                    <p className="mt-4 text-sm font-medium text-slate-500">
                        Loading room details...
                    </p>
                </div>
            </div>
        );
    }

    if (error || !room) {
        return (
            <div className="min-h-screen bg-[#F4F8FC] flex items-center justify-center px-4">
                <div className="w-full max-w-md rounded-3xl bg-white p-8 text-center shadow-xl border border-slate-100">
                    <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-50 text-red-500">
                        <FaInfoCircle className="text-2xl" />
                    </div>
                    <h2 className="mt-5 text-2xl font-bold text-[#0F172A]">
                        Room not found
                    </h2>
                    <p className="mt-2 text-sm text-slate-500">
                        We could not load this room. Please return to the previous page.
                    </p>
                    <button
                        onClick={() => navigate(-1)}
                        className="mt-6 inline-flex items-center gap-2 rounded-xl bg-[#174EA6] px-6 py-3 text-sm font-semibold text-white hover:bg-[#123D85] transition"
                    >
                        <FaArrowLeft />
                        Go Back
                    </button>
                </div>
            </div>
        );
    }

    let images = [];
    try {
        const parsed = room.images
            ? typeof room.images === "string"
                ? JSON.parse(room.images)
                : room.images
            : [];
        images = Array.isArray(parsed) ? parsed : [];
    } catch {
        images = [];
    }

    let amenities = [];
    try {
        const parsed = room.amenities
            ? typeof room.amenities === "string"
                ? JSON.parse(room.amenities)
                : room.amenities
            : [];
        amenities = Array.isArray(parsed)
            ? parsed
            : typeof parsed === "string"
                ? parsed.split(",").map((item) => item.trim()).filter(Boolean)
                : [];
    } catch {
        amenities =
            typeof room.amenities === "string"
                ? room.amenities.split(",").map((item) => item.trim()).filter(Boolean)
                : [];
    }

    const allImages = [...new Set([room.image, ...images].filter(Boolean))];
    const user = JSON.parse(localStorage.getItem("user") || "null");

    const getNights = () => {
        if (!checkIn || !checkOut) return 1;

        const start = new Date(checkIn);
        const end = new Date(checkOut);
        const diffTime = end - start;
        const nights = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        return nights > 0 ? nights : 1;
    };

    const getTourismFee = () => {
        return Number(room?.hotel?.rating) === 5 ? 20 : 10;
    };

    const activeSeason = room?.seasonal_prices?.find((season) => {
        const date = new Date(selectedDate);
        return (
            date >= new Date(season.start_date) &&
            date <= new Date(season.end_date)
        );
    });

    const nightlyRate = (() => {
        if (!room) return 0;

        let finalPrice = Number(room.price || 0);

        if (room.seasonal_prices?.length > 0) {
            const date = new Date(selectedDate);

            const match = room.seasonal_prices.find((season) => {
                const start = new Date(season.start_date);
                const end = new Date(season.end_date);
                return date >= start && date <= end;
            });

            if (match) {
                finalPrice = Number(match.price || finalPrice);
            }
        }

        if (
            room.discount_price &&
            Number(room.discount_price) < finalPrice
        ) {
            finalPrice = Number(room.discount_price);
        }

        return finalPrice;
    })();

    const getFinalPriceBreakdown = () => {
        const base = nightlyRate;
        const nights = getNights();
        const serviceCharge = 0.1;
        const vat = 0.05;
        const tourismFee = getTourismFee();
        const nightlyWithTax =
            base + base * serviceCharge + base * vat + tourismFee;

        return {
            base,
            nights,
            serviceCharge,
            vat,
            tourismFee,
            nightlyWithTax,
            total: Math.round(nightlyWithTax * nights),
        };
    };

    const roundPrice = (value) => Math.round(Number(value || 0));

    const isDiscounted =
        room?.discount_price &&
        Number(room.discount_price) < Number(room.price);

    const savedAmount = isDiscounted
        ? Number(room.price) - Number(room.discount_price)
        : 0;

    const discountPercent = isDiscounted
        ? Math.round(
            ((Number(room.price) - Number(room.discount_price)) /
                Number(room.price)) *
            100
        )
        : 0;

    const isSeasonal = Boolean(activeSeason?.price);

    const openGallery = (index) => {
        setModalIndex(index);
        setActiveImage(allImages[index]);
        setShowModal(true);
    };

    const nextImage = () => {
        if (!allImages.length) return;

        setModalIndex((prev) => {
            const nextIndex =
                prev === allImages.length - 1 ? 0 : prev + 1;

            setActiveImage(allImages[nextIndex]);

            return nextIndex;
        });
    };

    const previousImage = () => {
        if (!allImages.length) return;

        setModalIndex((prev) => {
            const previousIndex =
                prev === 0 ? allImages.length - 1 : prev - 1;

            setActiveImage(allImages[previousIndex]);

            return previousIndex;
        });
    };

    const handleBooking = async () => {
        if (!name || !email || !phone || !checkIn || !checkOut) {
            alert("Please fill all fields");
            return;
        }

        if (new Date(checkOut) <= new Date(checkIn)) {
            alert("Check-out must be after Check-in");
            return;
        }

        if (!executeRecaptcha) {
            alert("Captcha not ready. Refresh page.");
            return;
        }

        try {
            setBookingLoading(true);

            const token = await executeRecaptcha("room_booking");
            const breakdown = getFinalPriceBreakdown();

            const bookingData = {
                user_id: user?.id,
                hotel_id: room?.hotel?.id ?? room?.hotel_id,
                room_id: room.id,
                room_name: room.name || `Room ${room.room_number}`,
                room_price: room.price,
                discount_price: room.discount_price || null,
                name,
                email,
                phone,
                check_in: checkIn,
                check_out: checkOut,
                guests,
                season_name: activeSeason?.season_name || null,
                season_start: activeSeason?.start_date || null,
                season_end: activeSeason?.end_date || null,
                total_amount: breakdown.total,
                nights: breakdown.nights,
                captcha: token,
            };

            const res = await axios.post(
                "https://dubaihotelrates.com/api/hotel-payment",
                bookingData
            );

            if (res.data.payment_url) {
                window.location.href = res.data.payment_url;
            } else {
                alert("Payment URL not received");
            }
        } catch (err) {
            console.error(err);

            if (err.response) {
                alert(
                    `❌ ${err.response.data.error || err.response.data.message || "Booking failed"
                    }`
                );
            } else {
                alert("❌ Server error");
            }
        } finally {
            setBookingLoading(false);
        }
    };

    const finalNightly = roundPrice(getFinalPriceBreakdown().nightlyWithTax);
    const stayTotal = roundPrice(getFinalPriceBreakdown().total);

    return (
        <>
            <Helmet>
                <title>{room.meta_title || room.name}</title>

                <meta
                    name="description"
                    content={room.meta_description || room.description}
                />

                <link
                    rel="canonical"
                    href={`https://dubaihotelrates.com/hotels/${room.hotel?.slug}/${room.id}`}
                />

                <meta
                    property="og:title"
                    content={room.meta_title || room.name}
                />
                <meta
                    property="og:description"
                    content={room.meta_description || room.description}
                />
                <meta
                    property="og:url"
                    content={`https://dubaihotelrates.com/hotels/${room.hotel?.slug}/${room.id}`}
                />
                <meta
                    property="og:image"
                    content={
                        room?.image
                            ? `${ROOM_IMG}/${room.image}`
                            : "https://dubaihotelrates.com/images/default-og.png"
                    }
                />
                <meta property="og:type" content="website" />

                <meta name="twitter:card" content="summary_large_image" />
                <meta
                    name="twitter:title"
                    content={room.meta_title || room.name}
                />
                <meta
                    name="twitter:description"
                    content={room.meta_description || room.description}
                />
                <meta
                    name="twitter:image"
                    content={
                        room?.image
                            ? `${ROOM_IMG}/${room.image}`
                            : "https://dubaihotelrates.com/images/default-og.png"
                    }
                />

                <meta name="robots" content="index, follow" />
            </Helmet>

            <main className="min-h-screen bg-[#F4F8FC] text-[#0F172A]">

                {/* =========================================================
                    ROOM HERO
                ========================================================== */}
                <section className="relative w-full overflow-hidden bg-[#031F4F]">
                    <div className="relative h-[400px] sm:h-[440px] md:h-[480px] lg:h-[520px]">

                        {activeImage && (
                            <img
                                src={`${ROOM_IMG}/${activeImage}`}
                                alt={room.name || "Room"}
                                className="absolute inset-0 h-full w-full object-cover object-center"
                            />
                        )}

                        {/* Homepage-style overlay */}
                        <div className="absolute inset-0 bg-gradient-to-r from-[#031F4F]/90 via-[#073D82]/45 to-black/10" />
                        <div className="absolute inset-x-0 bottom-0 h-64 bg-gradient-to-t from-[#031F4F]/95 via-[#031F4F]/50 to-transparent" />
                        <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-[#062D67]/55 to-transparent" />

                        {/* Top navigation */}
                        <div className="absolute left-0 right-0 top-0 z-10">
                            <div className="mx-auto flex max-w-[1440px] items-center justify-between px-4 py-5 sm:px-6 lg:px-10 mt-32">
                                <div className="flex items-center gap-2 text-sm text-white/90">
                                    <button
                                        onClick={() => navigate("/")}
                                        className="inline-flex items-center gap-2 hover:text-[#FFD43B] transition"
                                    >
                                        <FaHome />
                                        Home
                                    </button>

                                    <FaChevronRight className="text-[10px] text-white/50" />

                                    <button
                                        onClick={() => {
                                            if (room.hotel?.slug) {
                                                navigate(`/hotels/${room.hotel.slug}`);
                                            }
                                        }}
                                        className="max-w-[180px] truncate hover:text-[#FFD43B] transition"
                                    >
                                        {room.hotel?.name || "Hotel"}
                                    </button>

                                    <FaChevronRight className="text-[10px] text-white/50" />

                                    <span className="max-w-[180px] truncate font-medium text-white">
                                        Room {room.room_number}
                                    </span>
                                </div>

                                <button
                                    onClick={() => navigate(-1)}
                                    className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-black/20 px-4 py-2 text-xs font-semibold text-white backdrop-blur-md hover:bg-white/10 transition"
                                >
                                    <FaArrowLeft />
                                    Back
                                </button>
                            </div>
                        </div>

                        {/* Hero content */}
                        <div className="absolute inset-x-0 bottom-0 z-10">
                            <div className="mx-auto max-w-[1440px] px-4 pb-8 sm:px-6 sm:pb-10 lg:px-10 lg:pb-20">
                                <div className="max-w-4xl">

                                    <div className="flex flex-wrap items-center gap-2">
                                        {room.hotel?.rating && (
                                            <span className="rounded-full bg-[#FFC928] px-3 py-1 text-xs font-bold text-[#0F172A]">
                                                ★ {room.hotel.rating} Star Hotel
                                            </span>
                                        )}

                                        {isSeasonal && (
                                            <span className="inline-flex items-center gap-1.5 rounded-full border border-orange-300/30 bg-orange-500/90 px-3 py-1 text-xs font-bold text-white">
                                                <FaFire />
                                                Limited Time Offer
                                            </span>
                                        )}

                                        {isDiscounted && (
                                            <span className="inline-flex items-center gap-1.5 rounded-full border border-green-300/30 bg-green-500/90 px-3 py-1 text-xs font-bold text-white">
                                                <FaTag />
                                                {discountPercent}% OFF
                                            </span>
                                        )}
                                    </div>

                                    <h1 className="mt-4 text-3xl font-extrabold leading-tight text-white sm:text-4xl md:text-5xl lg:text-6xl">
                                        {room.name || `Room ${room.room_number}`}
                                    </h1>

                                    <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-white/85">
                                        <span className="inline-flex items-center gap-2">
                                            <FaMapMarkerAlt className="text-[#FFC928]" />
                                            {room.hotel?.name || "Dubai Hotel"}
                                        </span>

                                        <span className="inline-flex items-center gap-2">
                                            <FaUsers className="text-[#FFC928]" />
                                            Up to {room.capacity || "-"} guests
                                        </span>

                                        <span className="inline-flex items-center gap-2">
                                            <FaBed className="text-[#FFC928]" />
                                            {room.bed_type || "Comfortable bedding"}
                                        </span>

                                        {room.size_sqm && (
                                            <span className="inline-flex items-center gap-2">
                                                <FaExpand className="text-[#FFC928]" />
                                                {room.size_sqm} m²
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Gallery controls */}
                        {allImages.length > 1 && (
                            <>
                                <button
                                    onClick={previousImage}
                                    className="absolute left-4 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-black/25 text-white backdrop-blur-md hover:bg-black/45 transition"
                                    aria-label="Previous image"
                                >
                                    <FaArrowLeft />
                                </button>

                                <button
                                    onClick={nextImage}
                                    className="absolute right-4 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-black/25 text-white backdrop-blur-md hover:bg-black/45 transition"
                                    aria-label="Next image"
                                >
                                    <FaArrowRight />
                                </button>
                            </>
                        )}
                    </div>

                    {/* Thumbnail strip */}
                    {allImages.length > 1 && (
                        <div className="absolute bottom-4 left-1/2 z-[9999] hidden -translate-x-1/2 gap-2 md:flex">
                            {allImages.slice(0, 6).map((img, index) => (
                                <button
                                    key={`${img}-${index}`}
                                    onClick={() => {
                                        setActiveImage(img);
                                        setModalIndex(index);
                                    }}
                                    className={`h-12 w-16 overflow-hidden rounded-lg border-2 transition ${activeImage === img
                                        ? "border-[#FFC928] scale-105"
                                        : "border-white/30 opacity-75 hover:opacity-100"
                                        }`}
                                >
                                    <img
                                        src={`${ROOM_IMG}/${img}`}
                                        alt={`Room ${index + 1}`}
                                        className="h-full w-full object-cover"
                                    />
                                </button>
                            ))}
                        </div>
                    )}
                </section>

                {/* =========================================================
                    QUICK ROOM INFO
                ========================================================== */}
                <section className="relative z-20 -mt-1 md:-mt-8">
                    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                        <div className="grid overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-xl sm:grid-cols-2 lg:grid-cols-4">

                            <div className="flex items-center gap-4 border-b p-5 sm:border-r lg:border-b-0">
                                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#EAF3FF] text-[#174EA6]">
                                    <FaUsers />
                                </div>
                                <div>
                                    <p className="text-xs font-medium text-slate-400">Guests</p>
                                    <p className="mt-0.5 font-bold text-[#0F172A]">
                                        {room.capacity || "-"} Guests
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-center gap-4 border-b p-5 lg:border-b-0 lg:border-r">
                                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#EAF3FF] text-[#174EA6]">
                                    <FaBed />
                                </div>
                                <div>
                                    <p className="text-xs font-medium text-slate-400">Bed Type</p>
                                    <p className="mt-0.5 font-bold text-[#0F172A]">
                                        {room.bed_type || "-"}
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-center gap-4 border-b p-5 sm:border-r sm:border-b-0">
                                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#FFF7D6] text-[#B88600]">
                                    <FaExpand />
                                </div>
                                <div>
                                    <p className="text-xs font-medium text-slate-400">Room Size</p>
                                    <p className="mt-0.5 font-bold text-[#0F172A]">
                                        {room.size_sqm ? `${room.size_sqm} m²` : "-"}
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-center gap-4 p-5">
                                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#EAF3FF] text-[#174EA6]">
                                    <FaShieldAlt />
                                </div>
                                <div>
                                    <p className="text-xs font-medium text-slate-400">Availability</p>
                                    <p className={`mt-0.5 font-bold ${room.is_available === false
                                        ? "text-red-500"
                                        : "text-green-600"
                                        }`}>
                                        {room.is_available === false
                                            ? "Unavailable"
                                            : "Available to Book"}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* =========================================================
                    MAIN CONTENT
                ========================================================== */}
                <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-14 lg:px-8">
                    <div className="grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_390px]">

                        {/* LEFT */}
                        <div className="space-y-8">

                            {/* Intro */}
                            <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm sm:p-8">
                                <div className="flex flex-wrap items-start justify-between gap-4">
                                    <div>
                                        <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#174EA6]">
                                            Room {room.room_number}
                                        </p>

                                        <h2 className="mt-2 text-2xl font-extrabold text-[#0F172A] sm:text-3xl">
                                            {room.name || "Comfortable Dubai Stay"}
                                        </h2>

                                        <p className="mt-2 text-sm text-slate-500">
                                            {room.hotel?.name || "Dubai Hotel"}
                                        </p>
                                    </div>

                                    <div className="rounded-2xl bg-[#F3F8FF] px-4 py-3 text-right">
                                        <p className="text-xs text-slate-400">From</p>
                                        <p className="text-xl font-extrabold text-[#174EA6]">
                                            {finalNightly.toLocaleString()} AED
                                        </p>
                                        <p className="text-[11px] text-slate-400">per night incl. fees</p>
                                    </div>
                                </div>

                                <div
                                    className="room-content prose prose-slate mt-7 max-w-none text-sm leading-7"
                                    dangerouslySetInnerHTML={{
                                        __html: room.description || "<p>Enjoy a comfortable stay with modern facilities and convenient access to Dubai.</p>",
                                    }}
                                />
                            </div>

                            {/* Gallery */}
                            {allImages.length > 0 && (
                                <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm sm:p-8">
                                    <div className="flex items-end justify-between gap-4">
                                        <div>
                                            <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#174EA6]">
                                                Explore the room
                                            </p>
                                            <h2 className="mt-1 text-2xl font-extrabold text-[#0F172A]">
                                                Room Gallery
                                            </h2>
                                        </div>

                                        <span className="hidden rounded-full bg-[#F3F8FF] px-3 py-1.5 text-xs font-semibold text-[#174EA6] sm:inline-flex">
                                            {allImages.length} Photos
                                        </span>
                                    </div>

                                    <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
                                        {allImages.map((img, index) => (
                                            <button
                                                key={`${img}-${index}`}
                                                onClick={() => openGallery(index)}
                                                className={`group relative overflow-hidden rounded-2xl ${index === 0 ? "col-span-2 row-span-2 h-64 sm:h-80" : "h-32 sm:h-40"
                                                    }`}
                                            >
                                                <img
                                                    src={`${ROOM_IMG}/${img}`}
                                                    alt={`${room.name || "Room"} ${index + 1}`}
                                                    className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                                                />
                                                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-70" />
                                                <div className="absolute bottom-3 left-3 flex items-center gap-2 text-xs font-semibold text-white">
                                                    <FaExpand />
                                                    View photo
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Amenities */}
                            <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm sm:p-8">
                                <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#174EA6]">
                                    Comfort & convenience
                                </p>
                                <h2 className="mt-1 text-2xl font-extrabold text-[#0F172A]">
                                    Room Amenities
                                </h2>

                                {amenities.length > 0 ? (
                                    <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                                        {amenities.map((amenity, index) => (
                                            <div
                                                key={`${amenity}-${index}`}
                                                className="flex items-center gap-3 rounded-2xl border border-slate-100 bg-[#F8FBFF] px-4 py-3.5"
                                            >
                                                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#EAF3FF] text-[#174EA6]">
                                                    <FaCheck className="text-xs" />
                                                </span>
                                                <span className="text-sm font-medium text-slate-700">
                                                    {amenity}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="mt-5 text-sm text-slate-400">
                                        No amenities listed for this room.
                                    </p>
                                )}
                            </div>

                            {/* Policies */}
                            <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm sm:p-8">
                                <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#174EA6]">
                                    Before you book
                                </p>
                                <h2 className="mt-1 text-2xl font-extrabold text-[#0F172A]">
                                    Room Policies
                                </h2>

                                <div className="mt-6 grid gap-3 sm:grid-cols-2">
                                    <div className="rounded-2xl bg-[#F8FBFF] p-4">
                                        <p className="text-xs font-medium text-slate-400">Cancellation</p>
                                        <p className="mt-1 text-sm font-semibold text-slate-700">
                                            {room.cancellation_policy || "Policy applies"}
                                        </p>
                                    </div>

                                    <div className="rounded-2xl bg-[#F8FBFF] p-4">
                                        <p className="text-xs font-medium text-slate-400">Breakfast</p>
                                        <p className="mt-1 text-sm font-semibold text-slate-700">
                                            {room.breakfast_included ? "Included" : "Not Included"}
                                        </p>
                                    </div>

                                    <div className="rounded-2xl bg-[#F8FBFF] p-4">
                                        <p className="text-xs font-medium text-slate-400">Smoking</p>
                                        <p className="mt-1 text-sm font-semibold text-slate-700">
                                            {room.smoking_allowed ? "Allowed" : "Not Allowed"}
                                        </p>
                                    </div>

                                    <div className="rounded-2xl bg-[#F8FBFF] p-4">
                                        <p className="text-xs font-medium text-slate-400">Availability</p>
                                        <p className={`mt-1 text-sm font-semibold ${room.is_available === false
                                            ? "text-red-500"
                                            : "text-green-600"
                                            }`}>
                                            {room.is_available === false ? "Unavailable" : "Available"}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Why this room */}
                            <div className="overflow-hidden rounded-3xl bg-[#002B45] p-7 text-white shadow-sm sm:p-9">
                                <div className="max-w-2xl">
                                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#FFC928]">
                                        A better stay
                                    </p>
                                    <h2 className="!text-white mt-2 text-2xl font-extrabold sm:text-3xl">
                                        Everything you need for a comfortable Dubai stay.
                                    </h2>
                                    <p className="mt-3 text-sm leading-6 text-white/70">
                                        Enjoy a carefully selected room with comfortable
                                        accommodation, convenient facilities and transparent
                                        pricing.
                                    </p>

                                    <div className="mt-6 grid gap-3 sm:grid-cols-3">
                                        {[
                                            "Great location",
                                            "Transparent pricing",
                                            "Booking support",
                                        ].map((item) => (
                                            <div
                                                key={item}
                                                className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-3 text-xs font-semibold"
                                            >
                                                <FaCheck className="text-[#FFC928]" />
                                                {item}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* =================================================
                            DESKTOP BOOKING CARD
                        ================================================== */}
                        <aside className="lg:sticky lg:top-24">
                            <div className="overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-xl">

                                <div className="bg-gradient-to-r from-[#174EA6] to-[#002B45] p-6 text-white">
                                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-white/60">
                                        Reserve this room
                                    </p>
                                    <h3 className="!text-white mt-2 text-xl font-extrabold">
                                        Your Dubai stay starts here
                                    </h3>

                                    <div className="mt-5 flex items-end gap-2">
                                        <span className="text-3xl font-extrabold">
                                            {finalNightly.toLocaleString()}
                                        </span>
                                        <span className="mb-1 text-sm text-white/70">
                                            AED / night
                                        </span>
                                    </div>
                                </div>

                                <div className="p-6">
                                    {isDiscounted && (
                                        <div className="mb-4 flex items-center justify-between rounded-2xl border border-green-100 bg-green-50 px-4 py-3">
                                            <span className="flex items-center gap-2 text-xs font-semibold text-green-700">
                                                <FaTag />
                                                {discountPercent}% OFF
                                            </span>
                                            <span className="text-xs font-medium text-green-600">
                                                Save {savedAmount.toLocaleString()} AED
                                            </span>
                                        </div>
                                    )}

                                    {isSeasonal && (
                                        <div className="mb-4 rounded-2xl border border-orange-100 bg-orange-50 p-4">
                                            <p className="flex items-center gap-2 text-sm font-bold text-orange-700">
                                                <FaFire />
                                                Seasonal offer active
                                            </p>
                                            <p className="mt-1 text-xs leading-5 text-orange-600">
                                                Dynamic pricing may change based on demand and availability.
                                            </p>
                                        </div>
                                    )}

                                    <div className="space-y-4">
                                        <div>
                                            <label className="mb-1.5 block text-xs font-semibold text-slate-600">
                                                Check-in
                                            </label>
                                            <div className="relative">
                                                <FaCalendarAlt className="absolute left-3 top-1/2 -translate-y-1/2 text-[#174EA6]" />
                                                <input
                                                    type="date"
                                                    value={checkIn}
                                                    min={today}
                                                    onChange={(e) => {
                                                        setCheckIn(e.target.value);
                                                        if (
                                                            checkOut &&
                                                            e.target.value > checkOut
                                                        ) {
                                                            setCheckOut("");
                                                        }
                                                    }}
                                                    className="h-12 w-full rounded-xl border border-slate-200 bg-slate-50 pl-10 pr-3 text-sm outline-none focus:border-[#174EA6] focus:ring-2 focus:ring-[#174EA6]/10"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="mb-1.5 block text-xs font-semibold text-slate-600">
                                                Check-out
                                            </label>
                                            <div className="relative">
                                                <FaCalendarAlt className="absolute left-3 top-1/2 -translate-y-1/2 text-[#174EA6]" />
                                                <input
                                                    type="date"
                                                    value={checkOut}
                                                    min={checkIn || today}
                                                    onChange={(e) => setCheckOut(e.target.value)}
                                                    className="h-12 w-full rounded-xl border border-slate-200 bg-slate-50 pl-10 pr-3 text-sm outline-none focus:border-[#174EA6] focus:ring-2 focus:ring-[#174EA6]/10"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="mb-1.5 block text-xs font-semibold text-slate-600">
                                                Guests
                                            </label>
                                            <div className="relative">
                                                <FaUsers className="absolute left-3 top-1/2 -translate-y-1/2 text-[#174EA6]" />
                                                <input
                                                    type="number"
                                                    min="1"
                                                    max={room.capacity || undefined}
                                                    value={guests}
                                                    onChange={(e) =>
                                                        setGuests(e.target.value)
                                                    }
                                                    className="h-12 w-full rounded-xl border border-slate-200 bg-slate-50 pl-10 pr-3 text-sm outline-none focus:border-[#174EA6] focus:ring-2 focus:ring-[#174EA6]/10"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="my-6 border-t border-dashed border-slate-200" />

                                    <div className="space-y-3 text-sm">
                                        <div className="flex justify-between text-slate-500">
                                            <span>Room price / night</span>
                                            <span className="font-medium text-slate-700">
                                                {nightlyRate.toLocaleString()} AED
                                            </span>
                                        </div>

                                        <div className="flex justify-between text-slate-500">
                                            <span>Service charge (10%)</span>
                                            <span>
                                                {roundPrice(nightlyRate * 0.1).toLocaleString()} AED
                                            </span>
                                        </div>

                                        <div className="flex justify-between text-slate-500">
                                            <span>VAT (5%)</span>
                                            <span>
                                                {roundPrice(nightlyRate * 0.05).toLocaleString()} AED
                                            </span>
                                        </div>

                                        <div className="flex justify-between text-slate-500">
                                            <span>
                                                Tourism fee
                                                {Number(room?.hotel?.rating) === 5 &&
                                                    " (5-Star)"}
                                            </span>
                                            <span>{getTourismFee()} AED</span>
                                        </div>
                                    </div>

                                    <div className="mt-5 rounded-2xl bg-[#F3F8FF] p-4">
                                        <div className="flex items-center justify-between">
                                            <span className="text-xs font-semibold text-slate-500">
                                                Final price / night
                                            </span>
                                            <span className="text-lg font-extrabold text-[#174EA6]">
                                                {finalNightly.toLocaleString()} AED
                                            </span>
                                        </div>

                                        {checkIn && checkOut && (
                                            <div className="mt-3 border-t border-[#174EA6]/10 pt-3">
                                                <div className="flex justify-between text-xs text-slate-500">
                                                    <span>{getNights()} night(s)</span>
                                                    <span className="font-bold text-[#0F172A]">
                                                        {stayTotal.toLocaleString()} AED
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <button
                                        onClick={() => setShowBookingModal(true)}
                                        disabled={room.is_available === false}
                                        className={`mt-5 flex w-full items-center justify-center gap-2 rounded-xl py-3.5 text-sm font-bold text-white shadow-lg transition ${room.is_available === false
                                            ? "cursor-not-allowed bg-slate-300 shadow-none"
                                            : "bg-gradient-to-r from-[#174EA6] via-[#174EA6] to-[#FFC928] hover:shadow-xl hover:opacity-95"
                                            }`}
                                    >
                                        {room.is_available === false
                                            ? "Room Unavailable"
                                            : "Reserve This Room"}
                                        {room.is_available !== false && <FaArrowRight />}
                                    </button>

                                    <div className="mt-4 flex items-center justify-center gap-2 text-[11px] text-slate-400">
                                        <FaShieldAlt className="text-green-500" />
                                        Secure booking • No charge until checkout/payment
                                    </div>

                                    <p className="mt-2 text-center text-[11px] text-slate-400">
                                        {room.cancellation_policy === "free"
                                            ? "Free cancellation available"
                                            : "Cancellation policy applies"}
                                    </p>
                                </div>
                            </div>
                        </aside>
                    </div>
                </section>

                {/* =========================================================
                    MOBILE BOOKING BAR
                ========================================================== */}
                <div className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 p-3 shadow-[0_-8px_30px_rgba(15,23,42,0.12)] backdrop-blur-md lg:hidden">
                    <div className="mx-auto flex max-w-7xl items-center gap-3">
                        <div className="min-w-0 flex-1">
                            <p className="truncate text-xs font-medium text-slate-400">
                                From
                            </p>
                            <p className="text-lg font-extrabold text-[#174EA6]">
                                {finalNightly.toLocaleString()} AED
                                <span className="ml-1 text-[10px] font-medium text-slate-400">
                                    / night
                                </span>
                            </p>
                        </div>

                        <button
                            onClick={() => setShowBookingModal(true)}
                            disabled={room.is_available === false}
                            className="rounded-xl bg-gradient-to-r from-[#174EA6] to-[#FFC928] px-5 py-3 text-xs font-bold text-white shadow-lg disabled:bg-slate-300"
                        >
                            {room.is_available === false
                                ? "Unavailable"
                                : "Reserve Now"}
                        </button>
                    </div>
                </div>
            </main>

            {/* =============================================================
                FULLSCREEN GALLERY MODAL
            ============================================================== */}
            {showModal && allImages.length > 0 && (
                <div className="fixed inset-0 z-[100] bg-[#020817]/95">
                    <button
                        onClick={() => setShowModal(false)}
                        className="absolute right-5 top-20 z-20 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
                        aria-label="Close gallery"
                    >
                        <FaTimes />
                    </button>

                    {allImages.length > 1 && (
                        <>
                            <button
                                onClick={previousImage}
                                className="absolute left-4 top-1/2 z-20 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur-md hover:bg-white/20 sm:left-8"
                            >
                                <FaArrowLeft />
                            </button>

                            <button
                                onClick={nextImage}
                                className="absolute right-4 top-1/2 z-20 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur-md hover:bg-white/20 sm:right-8"
                            >
                                <FaArrowRight />
                            </button>
                        </>
                    )}

                    <div className="flex h-full items-center justify-center p-6 sm:p-12">
                        <img
                            src={`${ROOM_IMG}/${allImages[modalIndex]}`}
                            alt={room.name || "Room"}
                            className="max-h-[82vh] max-w-[92vw] object-contain rounded-2xl shadow-2xl"
                        />
                    </div>

                    <div className="absolute bottom-6 left-1/2 -translate-x-1/2 rounded-full bg-white/10 px-4 py-2 text-xs font-semibold text-white backdrop-blur-md">
                        {modalIndex + 1} / {allImages.length}
                    </div>
                </div>
            )}

            {/* =============================================================
                BOOKING MODAL
            ============================================================== */}
            {showBookingModal && (
                <div className="fixed inset-0 z-[9999] overflow-y-auto bg-[#020817]/70 px-3 py-6 backdrop-blur-sm sm:px-5 sm:py-8">

                    <div className="flex min-h-full items-center justify-center">

                        <div className="relative my-4 w-full max-w-2xl overflow-hidden rounded-3xl bg-white shadow-2xl sm:my-6">
                            <button
                                onClick={() => setShowBookingModal(false)}
                                className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-900"
                            >
                                <FaTimes />
                            </button>

                            <div className="bg-gradient-to-r from-[#174EA6] to-[#002B45] px-6 py-7 text-white sm:px-8">
                                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-white/60">
                                    Complete your reservation
                                </p>

                                <h3 className="!text-white mt-2 pr-10 text-2xl font-extrabold">
                                    Book {room.name || `Room ${room.room_number}`}
                                </h3>

                                <p className="mt-2 text-xs text-white/70">
                                    Final price includes applicable taxes and fees.
                                </p>
                            </div>

                            <div className="p-5 sm:p-8">
                                <div className="grid gap-4 sm:grid-cols-2">

                                    <div>
                                        <label className="mb-1.5 block text-xs font-semibold text-slate-600">
                                            Your Name
                                        </label>
                                        <input
                                            type="text"
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            placeholder="Enter your name"
                                            className="h-12 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 text-sm outline-none focus:border-[#174EA6] focus:ring-2 focus:ring-[#174EA6]/10"
                                        />
                                    </div>

                                    <div>
                                        <label className="mb-1.5 block text-xs font-semibold text-slate-600">
                                            Email
                                        </label>
                                        <input
                                            type="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            placeholder="info@southtravels.com"
                                            className="h-12 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 text-sm outline-none focus:border-[#174EA6] focus:ring-2 focus:ring-[#174EA6]/10"
                                        />
                                    </div>

                                    <div>
                                        <label className="mb-1.5 block text-xs font-semibold text-slate-600">
                                            Phone
                                        </label>
                                        <input
                                            type="tel"
                                            value={phone}
                                            onChange={(e) => setPhone(e.target.value)}
                                            placeholder="+971 4 447 3839"
                                            className="h-12 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 text-sm outline-none focus:border-[#174EA6] focus:ring-2 focus:ring-[#174EA6]/10"
                                        />
                                    </div>

                                    <div>
                                        <label className="mb-1.5 block text-xs font-semibold text-slate-600">
                                            Guests
                                        </label>
                                        <input
                                            type="number"
                                            min="1"
                                            max={room.capacity || undefined}
                                            value={guests}
                                            onChange={(e) => setGuests(e.target.value)}
                                            className="h-12 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 text-sm outline-none focus:border-[#174EA6] focus:ring-2 focus:ring-[#174EA6]/10"
                                        />
                                    </div>

                                    <div>
                                        <label className="mb-1.5 block text-xs font-semibold text-slate-600">
                                            Check-in
                                        </label>
                                        <input
                                            type="date"
                                            value={checkIn}
                                            min={today}
                                            onChange={(e) => {
                                                setCheckIn(e.target.value);
                                                if (
                                                    checkOut &&
                                                    e.target.value > checkOut
                                                ) {
                                                    setCheckOut("");
                                                }
                                            }}
                                            className="h-12 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 text-sm outline-none focus:border-[#174EA6] focus:ring-2 focus:ring-[#174EA6]/10"
                                        />
                                    </div>

                                    <div>
                                        <label className="mb-1.5 block text-xs font-semibold text-slate-600">
                                            Check-out
                                        </label>
                                        <input
                                            type="date"
                                            value={checkOut}
                                            min={checkIn || today}
                                            onChange={(e) => setCheckOut(e.target.value)}
                                            className="h-12 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 text-sm outline-none focus:border-[#174EA6] focus:ring-2 focus:ring-[#174EA6]/10"
                                        />
                                    </div>
                                </div>

                                <div className="mt-6 rounded-2xl border border-slate-100 bg-[#F8FBFF] p-5">
                                    <div className="flex items-center justify-between">
                                        <p className="text-sm font-bold text-[#0F172A]">
                                            Price Summary
                                        </p>
                                        <span className="rounded-full bg-[#EAF3FF] px-3 py-1 text-[10px] font-bold text-[#174EA6]">
                                            {getNights()} night(s)
                                        </span>
                                    </div>

                                    <div className="mt-4 space-y-2.5 text-sm">
                                        <div className="flex justify-between text-slate-500">
                                            <span>Base price / night</span>
                                            <span>{nightlyRate.toLocaleString()} AED</span>
                                        </div>

                                        <div className="flex justify-between text-slate-500">
                                            <span>VAT (5%)</span>
                                            <span>{roundPrice(nightlyRate * 0.05).toLocaleString()} AED</span>
                                        </div>

                                        <div className="flex justify-between text-slate-500">
                                            <span>Service charge (10%)</span>
                                            <span>{roundPrice(nightlyRate * 0.1).toLocaleString()} AED</span>
                                        </div>

                                        <div className="flex justify-between text-slate-500">
                                            <span>Tourism fee</span>
                                            <span>{getTourismFee()} AED</span>
                                        </div>

                                        <div className="my-3 border-t border-slate-200" />

                                        <div className="flex justify-between font-bold text-[#0F172A]">
                                            <span>Final price / night</span>
                                            <span className="text-[#174EA6]">
                                                {finalNightly.toLocaleString()} AED
                                            </span>
                                        </div>

                                        {checkIn && checkOut && (
                                            <div className="flex justify-between pt-1 text-lg font-extrabold text-[#0F172A]">
                                                <span>Total stay</span>
                                                <span>{stayTotal.toLocaleString()} AED</span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="mt-4 rounded-2xl border border-[#174EA6]/10 bg-[#EAF3FF]/50 p-4">
                                    <div className="flex gap-3">
                                        <FaShieldAlt className="mt-0.5 shrink-0 text-[#174EA6]" />
                                        <div>
                                            <p className="text-xs font-bold text-[#174EA6]">
                                                Secure booking
                                            </p>
                                            <p className="mt-1 text-[11px] leading-5 text-slate-500">
                                                Your booking request is protected and
                                                payment is handled securely.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <button
                                    onClick={handleBooking}
                                    disabled={bookingLoading || room.is_available === false}
                                    className={`mt-6 flex h-13 w-full items-center justify-center gap-2 rounded-xl py-3.5 text-sm font-bold text-white shadow-lg transition ${bookingLoading || room.is_available === false
                                        ? "cursor-not-allowed bg-slate-300 shadow-none"
                                        : "bg-gradient-to-r from-[#174EA6] via-[#174EA6] to-[#FFC928] hover:shadow-xl"
                                        }`}
                                >
                                    {bookingLoading ? (
                                        <>
                                            <span className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                                            Processing...
                                        </>
                                    ) : (
                                        <>
                                            Confirm Booking
                                            <FaArrowRight />
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
